import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'

/**
 * Represents a blog feed item from the RSS feed.
 * Maps to Types::BlogFeedType in the GraphQL schema.
 */
export interface BlogFeedItem {
  /** The title of the blog post (required) */
  title: string
  /** The URL of the blog post (required) */
  url: string
  /** The publication date of the blog post */
  published?: string
  /** The summary/description of the blog post */
  summary?: string
  /** The main image URL of the blog post */
  image?: string
  /** The thumbnail image URL of the blog post */
  thumbnail?: string
}

const THIRTY_MINUTES = 1000 * 60 * 30

const GET_BLOG_FEED = gql`
  query GetBlogFeed {
    blogFeed {
      title
      url
      summary
      published
      image
      thumbnail
    }
  }
`

interface BlogFeedQueryResponse {
  blogFeed: BlogFeedItem[] | null
}

/**
 * Hook to fetch blog feed items from the GraphQL API.
 * Uses the backend's cached RSS feed data for better performance and reliability.
 *
 * The backend returns an empty array if:
 * - The blog_feed feature flag is disabled for the current brand
 * - The current brand is not defined
 * - The cached feed is not available
 * - An error occurs during fetch
 *
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns A query result containing an array of blog feed items
 */
export function useBlogFeedApi(enabled: boolean = true): UseQueryResult<BlogFeedItem[], Error> {
  const client = useGraphQLClient()

  return useQuery<BlogFeedItem[], Error>({
    queryKey: ['blogFeed', 'v4'],
    queryFn: async () => {
      const { blogFeed } = await client.request<BlogFeedQueryResponse>(GET_BLOG_FEED)
      // Handle nullable response from GraphQL (type [Types::BlogFeedType], null: true)
      return blogFeed || []
    },
    enabled,
    staleTime: THIRTY_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 1,
  })
}
